import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { UserSettings, setSettings, setLoading, setError } from "../app/stores/user-settings.slice";
import { useIndexedDBContext } from "../providers/indexed-db-provider";

const DEFAULT_USER_SETTINGS: UserSettings = {
    id: "default",
    theme: "light",
    language: "en",
    notifications: true,
    applicationGridPositions: {},
};

export function useUserSettings() {
    const dispatch = useDispatch();
    const { userSettingsDb } = useIndexedDBContext();
    const { data: userSettings, isLoading, error, addItem, updateItem, getAllItems } = userSettingsDb;
    const isInitialized = useRef(false);

    useEffect(() => {
        const initializeUserSettings = async () => {
            if (isInitialized.current) return;

            
            dispatch(setLoading(true));

            try {
                
                const currentSettings = await getAllItems();
                

                if (currentSettings.length === 0) {
                    
                    // If no settings exist, add default ones
                    await addItem(DEFAULT_USER_SETTINGS);
                    // After adding default settings, get the updated list
                    
                    const updatedSettings = await getAllItems();
                    
                    dispatch(setSettings(updatedSettings[0]));
                } else {
                    
                    dispatch(setSettings(currentSettings[0]));
                }

                isInitialized.current = true;
                
            } catch (err) {
                
                dispatch(setError(err instanceof Error ? err.message : "Failed to load user settings"));
            } finally {
                dispatch(setLoading(false));
            }
        };

        initializeUserSettings();
    }, [dispatch, getAllItems, addItem]);

    const updateSettings = async (settings: Partial<UserSettings>) => {
        const currentSettings = userSettings[0] || DEFAULT_USER_SETTINGS;
        const updatedSettings: UserSettings = {
            ...currentSettings,
            ...settings,
        };
        await updateItem(updatedSettings);
        const updatedSettingsList = await getAllItems();
        dispatch(setSettings(updatedSettingsList[0]));
    };

    return {
        userSettings: userSettings[0] || DEFAULT_USER_SETTINGS,
        isLoading,
        error,
        updateSettings,
    };
}
